<template>
    <v-timeline
        ref="timeline"
        dense>
        <!-- Colored pill that slides behind the active item. -->
        <v-timeline-item
            class="timeline-cursor ease-animation"
            :style="{ top: `${cursorTop}px` }"
            :color="current.color"
            @transitionend.native.self="land">
            <template #icon>
                <v-btn
                    small
                    fab
                    class="ease-animation"
                    :color="current.color"
                    tabindex="-1"
                    aria-hidden="true">
                    <v-icon color="transparent">{{ current.icon }}</v-icon>
                </v-btn>
            </template>
            <v-card
                v-if="$vuetify.breakpoint.mdAndUp"
                class="ease-animation pointer"
                dark
                :color="current.color"
                :width="cursorWidth"
                elevation="0"
                rounded="xl">
                <v-card
                    elevation="0"
                    class="ease-animation clip"
                    color="transparent">
                    <v-card-title class="text-h6 font-weight-light transparent--text text-no-wrap">{{ current.title }}</v-card-title>
                </v-card>
            </v-card>
        </v-timeline-item>

        <v-timeline-item
            v-for="(item, i) in items"
            ref="items"
            :key="item.title"
            :class="{ 'timeline-target': i === value }"
            :color="i === value ? 'transparent' : 'surface'"
            fill-dot>
            <template #icon>
                <v-btn
                    small
                    fab
                    :color="i === landed ? item.color : 'transparent'"
                    :aria-label="item.title"
                    @click="select(i)">
                    <v-icon :color="i === landed ? 'surface' : 'white'">{{ item.icon }}</v-icon>
                </v-btn>
            </template>
            <v-card
                v-if="$vuetify.breakpoint.mdAndUp"
                class="timeline-label clip"
                dark
                color="transparent"
                elevation="0"
                rounded="xl"
                max-width="fit-content">
                <v-card
                    elevation="0"
                    class="clip"
                    color="transparent"
                    @click="select(i)">
                    <v-card-title
                        class="text-h6"
                        :class="i === landed ? 'surface--text' : 'white--text'">
                        {{ item.title }}
                    </v-card-title>
                </v-card>
            </v-card>
        </v-timeline-item>
    </v-timeline>
</template>

<script>
/** Vertical tab list of the resume sections. Use with v-model (active index). */
export default {
    name: 'ResumeTimeline',
    props: {
        items: { type: Array, required: true },
        value: { type: Number, default: 0 },
    },
    data: (vm) => ({
        cursorTop: 0,
        cursorWidth: null,
        // Highlighted item: lags behind `value` until the cursor has landed on it.
        landed: vm.value,
        observer: null,
    }),
    computed: {
        current() {
            return this.items[this.value];
        },
    },
    watch: {
        value() {
            // Drop the old highlight as the cursor leaves; land() sets the new one on arrival.
            this.landed = null;
            this.moveCursor();
        },
    },
    mounted() {
        // Item positions change with the breakpoint and font loading, so follow the timeline's size.
        this.observer = new ResizeObserver(() => this.moveCursor());
        this.observer.observe(this.$refs.timeline.$el);
    },
    beforeDestroy() {
        this.observer.disconnect();
    },
    methods: {
        select(index) {
            this.$emit('input', index);
        },
        land({ propertyName }) {
            if (propertyName === 'top') this.landed = this.value;
        },
        moveCursor() {
            // The cursor and the items share the same offset parent.
            const item = this.$refs.items[this.value].$el;
            this.cursorTop = item.offsetTop;
            // An auto width can't be transitioned, so size the pill to the label explicitly.
            this.cursorWidth = item.querySelector('.timeline-label')?.offsetWidth ?? null;
        },
    },
};
</script>

<style scoped>
.timeline-cursor {
    position: absolute;
}
/* The dots sit above the cursor: clear the target's dot as the cursor heads to it, and refill the one it leaves. */
.v-timeline ::v-deep .v-timeline-item__dot,
.v-timeline ::v-deep .v-timeline-item__inner-dot {
    transition: background-color 0.3s cubic-bezier(0.11, 1.04, 0.8, 0.99);
}
.v-timeline-item.timeline-target ::v-deep .v-timeline-item__dot {
    background-color: transparent;
}
.ease-animation {
    transition: all 0.3s cubic-bezier(0.11, 1.04, 0.8, 0.99);
}
.clip {
    overflow: hidden;
}
.pointer {
    cursor: pointer;
}
</style>
